
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Book, Quote, Scale, Brain, MessageCircle, Heart } from "lucide-react";
import ResponseActions from "./ResponseActions";
import { InterfaithResponse } from "./types";
import { Objections } from "./Objections";
import { ScriptureReferences } from "./ScriptureReferences";

interface ResponseDisplayProps {
  response: InterfaithResponse;
  isSaving: boolean;
  onSave: () => void;
  onShareEmail: () => void;
  onShareSMS: () => void;
  onShareFacebook: () => void;
  faithGroup?: string;
}

const ResponseDisplay: React.FC<ResponseDisplayProps> = ({ 
  response, 
  isSaving, 
  onSave, 
  onShareEmail, 
  onShareSMS, 
  onShareFacebook,
  faithGroup
}) => {
  // Validate response sections to avoid rendering empty components
  const hasDirectAnswer = !!response?.directAnswer?.trim();
  const hasBiblicalFoundation = !!response?.biblicalFoundation?.trim();
  const hasScriptures = Array.isArray(response?.scriptureReferences) && 
                      response.scriptureReferences.length > 0;
  const hasComparisonOfBeliefs = !!response?.comparisonOfBeliefs?.trim();
  const hasRefutation = !!response?.refutationFromTheirTexts?.trim();
  const hasTheologicalReasoning = !!response?.theologicalReasoning?.trim();                 
  const hasObjections = Array.isArray(response?.commonObjections) && 
                      response.commonObjections.length > 0;
  const hasFinalAppeal = !!response?.finalAppeal?.trim();

  // Helper function to clean content by removing ### markers and other formatting issues
  const cleanContent = (content: string): string => {
    if (!content) return '';
    
    return content
      .replace(/###/g, '')  // Remove ### markers
      .replace(/\*\*/g, '') // Remove markdown bold syntax
      .replace(/\n{3,}/g, '\n\n') // Replace excess newlines
      .trim();
  };

  // Helper function to render text sections
  const renderTextSection = (title: string, content: string, icon: React.ReactNode, specialClass?: string) => {
    if (!content?.trim()) return null;
    
    // One additional check to prevent sections containing other section headers
    const possibleSectionHeaders = [
      'Biblical Foundation:', 'Comparison of Beliefs:', 
      'Refutation from Their Texts:', 'Theological Reasoning:', 
      'Final Appeal:'
    ];
    
    let cleanedContent = cleanContent(content);
    for (const header of possibleSectionHeaders) {
      if (cleanedContent.includes(header)) {
        cleanedContent = cleanedContent.split(header)[0].trim();
      }
    }
    
    if (!cleanedContent.trim()) return null;
    
    return (
      <Card className={`border-heaven-gold/10 ${specialClass || ''}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-heaven-gold text-lg">
            {icon}
            <span>{title}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-[#403E43] whitespace-pre-line text-left">{cleanedContent}</div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-playfair font-bold text-heaven-gold text-left mb-6">
        Your Response
      </h2>
      
      <ResponseActions 
        onSave={onSave}
        onShareEmail={onShareEmail}
        onShareSMS={onShareSMS}
        onShareFacebook={onShareFacebook}
        isSaving={isSaving}
      />
      
      {hasDirectAnswer && renderTextSection(
        "Direct Answer", 
        response.directAnswer, 
        <Quote className="h-5 w-5" />
      )}
      
      {hasBiblicalFoundation && renderTextSection(
        "Biblical Foundation", 
        response.biblicalFoundation, 
        <Book className="h-5 w-5" />
      )}
      
      {hasScriptures && (
        <ScriptureReferences 
          references={response.scriptureReferences} 
          contextualTitle="Scripture References"
        />
      )}
      
      {hasComparisonOfBeliefs && renderTextSection(
        "Comparison of Beliefs", 
        response.comparisonOfBeliefs, 
        <Scale className="h-5 w-5" />
      )}
      
      {hasRefutation && renderTextSection(
        "Refutation from Their Texts", 
        response.refutationFromTheirTexts, 
        <Book className="h-5 w-5" />
      )}
      
      {hasTheologicalReasoning && renderTextSection(
        "Logical & Theological Reasoning", 
        response.theologicalReasoning, 
        <Brain className="h-5 w-5" />
      )}
      
      {hasObjections && (
        <Objections 
          objections={response.commonObjections}
          faithGroup={faithGroup}
        />
      )}
      
      {hasFinalAppeal && renderTextSection(
        "Final Gospel Appeal", 
        response.finalAppeal, 
        <Heart className="h-5 w-5" />,
        "bg-heaven-gold/5"
      )}
      
      <ResponseActions 
        onSave={onSave}
        onShareEmail={onShareEmail}
        onShareSMS={onShareSMS}
        onShareFacebook={onShareFacebook}
        isSaving={isSaving}
      />
    </div>
  );
};

export default ResponseDisplay;
